import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Button, Dialog, ModalRef } from 'alurkerja-ui';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { Loading } from '@/components/Loading';
import {
  ILinkTestIssue,
  linkTestIssueSchema,
  TestRunDetailIssueService,
} from '@/api/testRunDetailIssue';
import { FaLink } from 'react-icons/fa';
import { SelectMultipleField } from '@/components/form/fields/SelectMultipleField';

type LinkTestIssueProps = {
  testRunDetailId: string;
  projectId: string;
  onSuccess?: () => void;
  selectedTestIssue?: string[];
};

export const LinkTestIssue: React.FC<LinkTestIssueProps> = ({
  testRunDetailId,
  projectId,
  onSuccess,
  selectedTestIssue = [],
}) => {
  const ref = useRef<ModalRef>(null);
  const form = useForm<ILinkTestIssue>({
    resolver: zodResolver(linkTestIssueSchema),
    values: {
      test_run_detail_id: testRunDetailId,
      test_issue_id: selectedTestIssue,
    },
  });

  const { data: testIssueOptions, isFetching: isFetchingTestIssues } =
    TestRunDetailIssueService.useGetTestIssue(projectId);

  const { mutate: linkTestIssue, isPending: isLinking } =
    TestRunDetailIssueService.useLink();

  const { mutate: clearLinkTestIssue, isPending: isClearing } =
    TestRunDetailIssueService.useClearLink();

  const onSubmit = (data: ILinkTestIssue) => {
    const testIssueIds: string[] = data.test_issue_id ? data.test_issue_id : [];

    linkTestIssue(
      { test_run_detail_id: testRunDetailId, test_issue_id: testIssueIds },
      {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test issue linked successfully',
            callback: () => {
              onSuccess?.();
              ref.current?.closeModal();
            },
          });
        },
        onError: e => {
          console.log(e);

          Dialog.error({
            title: 'Error',
            description: 'Failed to link test issue',
          });
        },
      },
    );
  };

  const onClearLink = () => {
    clearLinkTestIssue(
      { test_run_detail_id: testRunDetailId, test_issue_id: [] },
      {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test issue link deleted successfully',
            callback: () => {
              onSuccess?.();
              ref.current?.closeModal();
            },
          });
        },
        onError: e => {
          console.log(e);

          Dialog.error({
            title: 'Error',
            description: 'Failed to delete test issue link',
          });
        },
      },
    );
  };

  return (
    <Modal
      triggerButton={
        <Button className="bg-sky-400">
          <FaLink />
        </Button>
      }
      title="Link Test Issue"
    >
      {({ closeModal }) => (
        <FormContainer form={form} onSubmit={onSubmit}>
          {isFetchingTestIssues ? (
            <Loading />
          ) : (
            <div className="flex flex-col gap-4 p-4">
              <SelectMultipleField
                options={testIssueOptions?.testIssue}
                name="test_issue_id"
                label="Test Issue"
                hasRequiredAsterisk
                isSearchable
              />

              <FormFooter>
                <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
                <Button loading={isLinking}>Link</Button>
                <Button
                  color="red"
                  loading={isClearing}
                  type="button"
                  onClick={() => {
                    onClearLink();
                  }}
                  disabled={selectedTestIssue.length === 0}
                >
                  Clear Link
                </Button>
              </FormFooter>
            </div>
          )}
        </FormContainer>
      )}
    </Modal>
  );
};
